/**
 * Generated by orval
 * Do not edit manually.
 * IP Fabric API
 * IP Fabric API documentation. For details see [IP Fabric API documentation](https://docs.ipfabric.io/main/IP_Fabric_API/)
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  PostAimFlowPathLookupsSnapshotId202,
  PostAimFlowPathLookupsSnapshotIdBody,
  PostAimImportSnapshotId202,
  PostAimImportSnapshotIdBodyOne,
  PostAimImportSnapshotIdBodyTwo,
  PostAimIntegrationVerifyBody,
  PostAimIntegrationVerifyByIdBody,
  PostSnapshotAimIntegrationVerifyBody,
  PostTablesInventoryApplications200,
  PostTablesInventoryApplicationsBody,
  PostTablesInventoryApplicationsDevices200,
  PostTablesInventoryApplicationsDevicesBody,
  PostTablesInventoryApplicationsFlows200,
  PostTablesInventoryApplicationsFlowsBody,
  PostTablesInventoryApplicationsWorkloads200,
  PostTablesInventoryApplicationsWorkloadsBody,
} from '../../schemas';

import { apiClient } from '../../../apiClient';

/**
 * Schedules a background job that runs a path-lookup for AIM (Application Infrastructure Mapping) flows in the specified snapshot and persists which network devices each flow traverses. Provide exactly one of `flowId` (a single flow) or `applicationId` (expanded server-side to every flow whose source or destination workload belongs to that application). Requires a valid AIM license.
 * @summary Schedule AIM flow path-lookups
 */
export const postAimFlowPathLookupsSnapshotId = (
  snapshotId: string,
  postAimFlowPathLookupsSnapshotIdBody: PostAimFlowPathLookupsSnapshotIdBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostAimFlowPathLookupsSnapshotId202>({
    url: `/aim/flow-path-lookups/${snapshotId}`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postAimFlowPathLookupsSnapshotIdBody,
    signal,
  });
};

export const getPostAimFlowPathLookupsSnapshotIdMutationOptions = <
  TError = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAimFlowPathLookupsSnapshotId>>,
    TError,
    { snapshotId: string; data: PostAimFlowPathLookupsSnapshotIdBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postAimFlowPathLookupsSnapshotId>>,
  TError,
  { snapshotId: string; data: PostAimFlowPathLookupsSnapshotIdBody },
  TContext
> => {
  const mutationKey = ['postAimFlowPathLookupsSnapshotId'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postAimFlowPathLookupsSnapshotId>>,
    { snapshotId: string; data: PostAimFlowPathLookupsSnapshotIdBody }
  > = (props) => {
    const { snapshotId, data } = props ?? {};

    return postAimFlowPathLookupsSnapshotId(snapshotId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostAimFlowPathLookupsSnapshotIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof postAimFlowPathLookupsSnapshotId>>
>;
export type PostAimFlowPathLookupsSnapshotIdMutationBody =
  PostAimFlowPathLookupsSnapshotIdBody;
export type PostAimFlowPathLookupsSnapshotIdMutationError = void;

/**
 * @summary Schedule AIM flow path-lookups
 */
export const usePostAimFlowPathLookupsSnapshotId = <
  TError = void,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postAimFlowPathLookupsSnapshotId>>,
      TError,
      { snapshotId: string; data: PostAimFlowPathLookupsSnapshotIdBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof postAimFlowPathLookupsSnapshotId>>,
  TError,
  { snapshotId: string; data: PostAimFlowPathLookupsSnapshotIdBody },
  TContext
> => {
  const mutationOptions =
    getPostAimFlowPathLookupsSnapshotIdMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Schedules a background job to import AIM (Application Infrastructure Mapping) data into the specified snapshot. Accepts either `multipart/form-data` with CSV files or `application/json` with the rows inline. At least one non-empty section must be provided. Row-level validation of a JSON body is synchronous: errors are returned as `422` with the offending field path and row index, rather than surfacing later as a failed job. Cross-reference checks (for example an `applicationExternalId` that does not exist) still run in the background job.
 * @summary Import AIM data
 */
export const postAimImportSnapshotId = (
  snapshotId: string | '$last' | '$lastLocked' | '$prev',
  postAimImportSnapshotIdBody:
    | PostAimImportSnapshotIdBodyOne
    | PostAimImportSnapshotIdBodyTwo,
  signal?: AbortSignal,
) => {
  return apiClient<PostAimImportSnapshotId202>({
    url: `/aim/import/${snapshotId}`,
    method: 'POST',
    data: postAimImportSnapshotIdBody,
    signal,
  });
};

export const getPostAimImportSnapshotIdMutationOptions = <
  TError = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAimImportSnapshotId>>,
    TError,
    {
      snapshotId: string | '$last' | '$lastLocked' | '$prev';
      data: PostAimImportSnapshotIdBodyOne | PostAimImportSnapshotIdBodyTwo;
    },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postAimImportSnapshotId>>,
  TError,
  {
    snapshotId: string | '$last' | '$lastLocked' | '$prev';
    data: PostAimImportSnapshotIdBodyOne | PostAimImportSnapshotIdBodyTwo;
  },
  TContext
> => {
  const mutationKey = ['postAimImportSnapshotId'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postAimImportSnapshotId>>,
    {
      snapshotId: string | '$last' | '$lastLocked' | '$prev';
      data: PostAimImportSnapshotIdBodyOne | PostAimImportSnapshotIdBodyTwo;
    }
  > = (props) => {
    const { snapshotId, data } = props ?? {};

    return postAimImportSnapshotId(snapshotId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostAimImportSnapshotIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof postAimImportSnapshotId>>
>;
export type PostAimImportSnapshotIdMutationBody =
  | PostAimImportSnapshotIdBodyOne
  | PostAimImportSnapshotIdBodyTwo;
export type PostAimImportSnapshotIdMutationError = void;

/**
 * @summary Import AIM data
 */
export const usePostAimImportSnapshotId = <TError = void, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postAimImportSnapshotId>>,
      TError,
      {
        snapshotId: string | '$last' | '$lastLocked' | '$prev';
        data: PostAimImportSnapshotIdBodyOne | PostAimImportSnapshotIdBodyTwo;
      },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof postAimImportSnapshotId>>,
  TError,
  {
    snapshotId: string | '$last' | '$lastLocked' | '$prev';
    data: PostAimImportSnapshotIdBodyOne | PostAimImportSnapshotIdBodyTwo;
  },
  TContext
> => {
  const mutationOptions = getPostAimImportSnapshotIdMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Verifies that a stored AIM integration is reachable and that its credentials are accepted, without running a discovery. The integration is read from the global settings and its `apiSecret` is decrypted server-side, so the client never has to resend the secret.

Every body field is an optional override applied on top of the stored record, which lets unsaved form edits be tested before they are saved. A provided `apiSecret` must be plaintext.

Requires a valid AIM license.
 * @summary Test a stored AIM integration connection
 */
export const postAimIntegrationVerifyById = (
  id: string,
  postAimIntegrationVerifyByIdBody: PostAimIntegrationVerifyByIdBody,
  signal?: AbortSignal,
) => {
  return apiClient<void>({
    url: `/aim/integrations/${id}/verify`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postAimIntegrationVerifyByIdBody,
    signal,
  });
};

export const getPostAimIntegrationVerifyByIdMutationOptions = <
  TError = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAimIntegrationVerifyById>>,
    TError,
    { id: string; data: PostAimIntegrationVerifyByIdBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postAimIntegrationVerifyById>>,
  TError,
  { id: string; data: PostAimIntegrationVerifyByIdBody },
  TContext
> => {
  const mutationKey = ['postAimIntegrationVerifyById'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postAimIntegrationVerifyById>>,
    { id: string; data: PostAimIntegrationVerifyByIdBody }
  > = (props) => {
    const { id, data } = props ?? {};

    return postAimIntegrationVerifyById(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostAimIntegrationVerifyByIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof postAimIntegrationVerifyById>>
>;
export type PostAimIntegrationVerifyByIdMutationBody =
  PostAimIntegrationVerifyByIdBody;
export type PostAimIntegrationVerifyByIdMutationError = void;

/**
 * @summary Test a stored AIM integration connection
 */
export const usePostAimIntegrationVerifyById = <
  TError = void,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postAimIntegrationVerifyById>>,
      TError,
      { id: string; data: PostAimIntegrationVerifyByIdBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof postAimIntegrationVerifyById>>,
  TError,
  { id: string; data: PostAimIntegrationVerifyByIdBody },
  TContext
> => {
  const mutationOptions =
    getPostAimIntegrationVerifyByIdMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Verifies that the AIM data provider described by the request body is reachable and that its credentials are accepted, without running a discovery. Nothing is read from or written to the stored settings, so this also covers an integration that has not been saved yet.

`apiSecret` must be plaintext. To test a saved integration without resending its secret, use `POST /aim/integrations/{id}/verify` instead.

Requires a valid AIM license.
 * @summary Test an AIM integration connection
 */
export const postAimIntegrationVerify = (
  postAimIntegrationVerifyBody: PostAimIntegrationVerifyBody,
  signal?: AbortSignal,
) => {
  return apiClient<void>({
    url: `/aim/integrations/verify`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postAimIntegrationVerifyBody,
    signal,
  });
};

export const getPostAimIntegrationVerifyMutationOptions = <
  TError = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAimIntegrationVerify>>,
    TError,
    { data: PostAimIntegrationVerifyBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postAimIntegrationVerify>>,
  TError,
  { data: PostAimIntegrationVerifyBody },
  TContext
> => {
  const mutationKey = ['postAimIntegrationVerify'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postAimIntegrationVerify>>,
    { data: PostAimIntegrationVerifyBody }
  > = (props) => {
    const { data } = props ?? {};

    return postAimIntegrationVerify(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostAimIntegrationVerifyMutationResult = NonNullable<
  Awaited<ReturnType<typeof postAimIntegrationVerify>>
>;
export type PostAimIntegrationVerifyMutationBody = PostAimIntegrationVerifyBody;
export type PostAimIntegrationVerifyMutationError = void;

/**
 * @summary Test an AIM integration connection
 */
export const usePostAimIntegrationVerify = <TError = void, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postAimIntegrationVerify>>,
      TError,
      { data: PostAimIntegrationVerifyBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof postAimIntegrationVerify>>,
  TError,
  { data: PostAimIntegrationVerifyBody },
  TContext
> => {
  const mutationOptions = getPostAimIntegrationVerifyMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Verifies that an AIM integration stored in a snapshot's settings is reachable and that its credentials are accepted, without running a discovery. The snapshot-scoped counterpart of `POST /aim/integrations/{id}/verify`: the integration is read from the snapshot settings and its `apiSecret` is decrypted server-side, so the client never has to resend the secret.

Every body field is an optional override applied on top of the stored record, which lets unsaved form edits be tested before they are saved. A provided `apiSecret` must be plaintext.

Requires a valid AIM license.
 * @summary Test a stored snapshot AIM integration connection
 */
export const postSnapshotAimIntegrationVerify = (
  snapshotId: string,
  id: string,
  postSnapshotAimIntegrationVerifyBody: PostSnapshotAimIntegrationVerifyBody,
  signal?: AbortSignal,
) => {
  return apiClient<void>({
    url: `/snapshots/${snapshotId}/aim/integrations/${id}/verify`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postSnapshotAimIntegrationVerifyBody,
    signal,
  });
};

export const getPostSnapshotAimIntegrationVerifyMutationOptions = <
  TError = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postSnapshotAimIntegrationVerify>>,
    TError,
    {
      snapshotId: string;
      id: string;
      data: PostSnapshotAimIntegrationVerifyBody;
    },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postSnapshotAimIntegrationVerify>>,
  TError,
  {
    snapshotId: string;
    id: string;
    data: PostSnapshotAimIntegrationVerifyBody;
  },
  TContext
> => {
  const mutationKey = ['postSnapshotAimIntegrationVerify'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postSnapshotAimIntegrationVerify>>,
    {
      snapshotId: string;
      id: string;
      data: PostSnapshotAimIntegrationVerifyBody;
    }
  > = (props) => {
    const { snapshotId, id, data } = props ?? {};

    return postSnapshotAimIntegrationVerify(snapshotId, id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostSnapshotAimIntegrationVerifyMutationResult = NonNullable<
  Awaited<ReturnType<typeof postSnapshotAimIntegrationVerify>>
>;
export type PostSnapshotAimIntegrationVerifyMutationBody =
  PostSnapshotAimIntegrationVerifyBody;
export type PostSnapshotAimIntegrationVerifyMutationError = void;

/**
 * @summary Test a stored snapshot AIM integration connection
 */
export const usePostSnapshotAimIntegrationVerify = <
  TError = void,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postSnapshotAimIntegrationVerify>>,
      TError,
      {
        snapshotId: string;
        id: string;
        data: PostSnapshotAimIntegrationVerifyBody;
      },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof postSnapshotAimIntegrationVerify>>,
  TError,
  {
    snapshotId: string;
    id: string;
    data: PostSnapshotAimIntegrationVerifyBody;
  },
  TContext
> => {
  const mutationOptions =
    getPostSnapshotAimIntegrationVerifyMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Table shows applications discovered using the configured Microsegmentation integrations.
 * @summary Application inventory
 */
export const postTablesInventoryApplications = (
  postTablesInventoryApplicationsBody: PostTablesInventoryApplicationsBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostTablesInventoryApplications200>({
    url: `/tables/inventory/applications`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postTablesInventoryApplicationsBody,
    signal,
  });
};

export const getPostTablesInventoryApplicationsQueryKey = (
  postTablesInventoryApplicationsBody: PostTablesInventoryApplicationsBody,
) => {
  return [
    `/tables/inventory/applications`,
    postTablesInventoryApplicationsBody,
  ] as const;
};

export const getPostTablesInventoryApplicationsQueryOptions = <
  TData = Awaited<ReturnType<typeof postTablesInventoryApplications>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsBody: PostTablesInventoryApplicationsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplications>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getPostTablesInventoryApplicationsQueryKey(
      postTablesInventoryApplicationsBody,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof postTablesInventoryApplications>>
  > = ({ signal }) =>
    postTablesInventoryApplications(
      postTablesInventoryApplicationsBody,
      signal,
    );

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof postTablesInventoryApplications>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PostTablesInventoryApplicationsQueryResult = NonNullable<
  Awaited<ReturnType<typeof postTablesInventoryApplications>>
>;
export type PostTablesInventoryApplicationsQueryError = unknown;

export function usePostTablesInventoryApplications<
  TData = Awaited<ReturnType<typeof postTablesInventoryApplications>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsBody: PostTablesInventoryApplicationsBody,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplications>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesInventoryApplications>>,
          TError,
          Awaited<ReturnType<typeof postTablesInventoryApplications>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesInventoryApplications<
  TData = Awaited<ReturnType<typeof postTablesInventoryApplications>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsBody: PostTablesInventoryApplicationsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplications>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesInventoryApplications>>,
          TError,
          Awaited<ReturnType<typeof postTablesInventoryApplications>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesInventoryApplications<
  TData = Awaited<ReturnType<typeof postTablesInventoryApplications>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsBody: PostTablesInventoryApplicationsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplications>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Application inventory
 */

export function usePostTablesInventoryApplications<
  TData = Awaited<ReturnType<typeof postTablesInventoryApplications>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsBody: PostTablesInventoryApplicationsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplications>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getPostTablesInventoryApplicationsQueryOptions(
    postTablesInventoryApplicationsBody,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Table shows the network devices each application flow's path-lookup traverses. Populated by the AIM flow path-lookup calculation.
 * @summary Application Devices Inventory
 */
export const postTablesInventoryApplicationsDevices = (
  postTablesInventoryApplicationsDevicesBody: PostTablesInventoryApplicationsDevicesBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostTablesInventoryApplicationsDevices200>({
    url: `/tables/inventory/applications/devices`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postTablesInventoryApplicationsDevicesBody,
    signal,
  });
};

export const getPostTablesInventoryApplicationsDevicesQueryKey = (
  postTablesInventoryApplicationsDevicesBody: PostTablesInventoryApplicationsDevicesBody,
) => {
  return [
    `/tables/inventory/applications/devices`,
    postTablesInventoryApplicationsDevicesBody,
  ] as const;
};

export const getPostTablesInventoryApplicationsDevicesQueryOptions = <
  TData = Awaited<ReturnType<typeof postTablesInventoryApplicationsDevices>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsDevicesBody: PostTablesInventoryApplicationsDevicesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplicationsDevices>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getPostTablesInventoryApplicationsDevicesQueryKey(
      postTablesInventoryApplicationsDevicesBody,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof postTablesInventoryApplicationsDevices>>
  > = ({ signal }) =>
    postTablesInventoryApplicationsDevices(
      postTablesInventoryApplicationsDevicesBody,
      signal,
    );

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof postTablesInventoryApplicationsDevices>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PostTablesInventoryApplicationsDevicesQueryResult = NonNullable<
  Awaited<ReturnType<typeof postTablesInventoryApplicationsDevices>>
>;
export type PostTablesInventoryApplicationsDevicesQueryError = unknown;

export function usePostTablesInventoryApplicationsDevices<
  TData = Awaited<ReturnType<typeof postTablesInventoryApplicationsDevices>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsDevicesBody: PostTablesInventoryApplicationsDevicesBody,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplicationsDevices>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesInventoryApplicationsDevices>>,
          TError,
          Awaited<ReturnType<typeof postTablesInventoryApplicationsDevices>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesInventoryApplicationsDevices<
  TData = Awaited<ReturnType<typeof postTablesInventoryApplicationsDevices>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsDevicesBody: PostTablesInventoryApplicationsDevicesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplicationsDevices>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesInventoryApplicationsDevices>>,
          TError,
          Awaited<ReturnType<typeof postTablesInventoryApplicationsDevices>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesInventoryApplicationsDevices<
  TData = Awaited<ReturnType<typeof postTablesInventoryApplicationsDevices>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsDevicesBody: PostTablesInventoryApplicationsDevicesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplicationsDevices>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Application Devices Inventory
 */

export function usePostTablesInventoryApplicationsDevices<
  TData = Awaited<ReturnType<typeof postTablesInventoryApplicationsDevices>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsDevicesBody: PostTablesInventoryApplicationsDevicesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplicationsDevices>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getPostTablesInventoryApplicationsDevicesQueryOptions(
    postTablesInventoryApplicationsDevicesBody,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Table shows application flows between individual application workloads discovered using the configured Microsegmentation integrations.
 * @summary Application Flows Inventory
 */
export const postTablesInventoryApplicationsFlows = (
  postTablesInventoryApplicationsFlowsBody: PostTablesInventoryApplicationsFlowsBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostTablesInventoryApplicationsFlows200>({
    url: `/tables/inventory/applications/flows`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postTablesInventoryApplicationsFlowsBody,
    signal,
  });
};

export const getPostTablesInventoryApplicationsFlowsQueryKey = (
  postTablesInventoryApplicationsFlowsBody: PostTablesInventoryApplicationsFlowsBody,
) => {
  return [
    `/tables/inventory/applications/flows`,
    postTablesInventoryApplicationsFlowsBody,
  ] as const;
};

export const getPostTablesInventoryApplicationsFlowsQueryOptions = <
  TData = Awaited<ReturnType<typeof postTablesInventoryApplicationsFlows>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsFlowsBody: PostTablesInventoryApplicationsFlowsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplicationsFlows>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getPostTablesInventoryApplicationsFlowsQueryKey(
      postTablesInventoryApplicationsFlowsBody,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof postTablesInventoryApplicationsFlows>>
  > = ({ signal }) =>
    postTablesInventoryApplicationsFlows(
      postTablesInventoryApplicationsFlowsBody,
      signal,
    );

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof postTablesInventoryApplicationsFlows>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PostTablesInventoryApplicationsFlowsQueryResult = NonNullable<
  Awaited<ReturnType<typeof postTablesInventoryApplicationsFlows>>
>;
export type PostTablesInventoryApplicationsFlowsQueryError = unknown;

export function usePostTablesInventoryApplicationsFlows<
  TData = Awaited<ReturnType<typeof postTablesInventoryApplicationsFlows>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsFlowsBody: PostTablesInventoryApplicationsFlowsBody,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplicationsFlows>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesInventoryApplicationsFlows>>,
          TError,
          Awaited<ReturnType<typeof postTablesInventoryApplicationsFlows>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesInventoryApplicationsFlows<
  TData = Awaited<ReturnType<typeof postTablesInventoryApplicationsFlows>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsFlowsBody: PostTablesInventoryApplicationsFlowsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplicationsFlows>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesInventoryApplicationsFlows>>,
          TError,
          Awaited<ReturnType<typeof postTablesInventoryApplicationsFlows>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesInventoryApplicationsFlows<
  TData = Awaited<ReturnType<typeof postTablesInventoryApplicationsFlows>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsFlowsBody: PostTablesInventoryApplicationsFlowsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplicationsFlows>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Application Flows Inventory
 */

export function usePostTablesInventoryApplicationsFlows<
  TData = Awaited<ReturnType<typeof postTablesInventoryApplicationsFlows>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsFlowsBody: PostTablesInventoryApplicationsFlowsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplicationsFlows>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getPostTablesInventoryApplicationsFlowsQueryOptions(
    postTablesInventoryApplicationsFlowsBody,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Table shows application workloads discovered using the configured Micro-segmentation integrations.
 * @summary Application Workloads Inventory
 */
export const postTablesInventoryApplicationsWorkloads = (
  postTablesInventoryApplicationsWorkloadsBody: PostTablesInventoryApplicationsWorkloadsBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostTablesInventoryApplicationsWorkloads200>({
    url: `/tables/inventory/applications/workloads`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postTablesInventoryApplicationsWorkloadsBody,
    signal,
  });
};

export const getPostTablesInventoryApplicationsWorkloadsQueryKey = (
  postTablesInventoryApplicationsWorkloadsBody: PostTablesInventoryApplicationsWorkloadsBody,
) => {
  return [
    `/tables/inventory/applications/workloads`,
    postTablesInventoryApplicationsWorkloadsBody,
  ] as const;
};

export const getPostTablesInventoryApplicationsWorkloadsQueryOptions = <
  TData = Awaited<ReturnType<typeof postTablesInventoryApplicationsWorkloads>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsWorkloadsBody: PostTablesInventoryApplicationsWorkloadsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplicationsWorkloads>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getPostTablesInventoryApplicationsWorkloadsQueryKey(
      postTablesInventoryApplicationsWorkloadsBody,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof postTablesInventoryApplicationsWorkloads>>
  > = ({ signal }) =>
    postTablesInventoryApplicationsWorkloads(
      postTablesInventoryApplicationsWorkloadsBody,
      signal,
    );

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof postTablesInventoryApplicationsWorkloads>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PostTablesInventoryApplicationsWorkloadsQueryResult = NonNullable<
  Awaited<ReturnType<typeof postTablesInventoryApplicationsWorkloads>>
>;
export type PostTablesInventoryApplicationsWorkloadsQueryError = unknown;

export function usePostTablesInventoryApplicationsWorkloads<
  TData = Awaited<ReturnType<typeof postTablesInventoryApplicationsWorkloads>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsWorkloadsBody: PostTablesInventoryApplicationsWorkloadsBody,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplicationsWorkloads>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesInventoryApplicationsWorkloads>>,
          TError,
          Awaited<ReturnType<typeof postTablesInventoryApplicationsWorkloads>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesInventoryApplicationsWorkloads<
  TData = Awaited<ReturnType<typeof postTablesInventoryApplicationsWorkloads>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsWorkloadsBody: PostTablesInventoryApplicationsWorkloadsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplicationsWorkloads>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesInventoryApplicationsWorkloads>>,
          TError,
          Awaited<ReturnType<typeof postTablesInventoryApplicationsWorkloads>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesInventoryApplicationsWorkloads<
  TData = Awaited<ReturnType<typeof postTablesInventoryApplicationsWorkloads>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsWorkloadsBody: PostTablesInventoryApplicationsWorkloadsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplicationsWorkloads>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Application Workloads Inventory
 */

export function usePostTablesInventoryApplicationsWorkloads<
  TData = Awaited<ReturnType<typeof postTablesInventoryApplicationsWorkloads>>,
  TError = unknown,
>(
  postTablesInventoryApplicationsWorkloadsBody: PostTablesInventoryApplicationsWorkloadsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryApplicationsWorkloads>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getPostTablesInventoryApplicationsWorkloadsQueryOptions(
    postTablesInventoryApplicationsWorkloadsBody,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}
